import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { promoteAfterCapacityChange } from "@/lib/clinics.functions";
import {
  startConnectOnboarding,
  refreshConnectStatus,
  type ConnectStatus,
} from "@/lib/stripe-connect.functions";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTierGate } from "@/hooks/useSubscription";
import { UpgradeModal } from "@/components/billing/UpgradeModal";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type Clinic,
  type ClinicPaymentMode,
  PAYMENT_MODE_LABELS,
  fmtClinicPrice,
  publicClinicUrl,
} from "@/lib/clinics";
import { fmtBookingDate, fmtSlotTime, isoDatePlus } from "@/lib/booking";
import {
  Copy,
  CopyPlus,
  CreditCard,
  ExternalLink,
  GraduationCap,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Users,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/app/clinics")({
  head: () => ({
    meta: buildAppMeta({
      title: "Clinics — CoachQuick",
      description: "Run group clinics: public signup page, roster, waitlist, and attendee email.",
      path: "/app/clinics",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/clinics` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <ClinicsPage />
    </RequireAuth>
  ),
});

type LocationLite = { id: string; name: string };

type ClinicRow = Clinic & { clinic_registrations: { status: string }[] };

function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
}

/** "14:30:00" -> "14:30" for <input type="time"> */
const t5 = (t: string) => t.slice(0, 5);

const SKILL_LEVELS = ["All levels", "Beginner", "Intermediate", "Advanced", "2.5–3.0", "3.5+"];

type ClinicForm = {
  title: string;
  description: string;
  skill_level: string;
  clinic_date: string;
  start_time: string;
  end_time: string;
  location_id: string;
  capacity: string;
  price: string; // dollars, as typed
  payment_mode: ClinicPaymentMode;
};

const EMPTY_FORM: ClinicForm = {
  title: "",
  description: "",
  skill_level: "",
  clinic_date: "",
  start_time: "09:00",
  end_time: "10:30",
  location_id: "",
  capacity: "8",
  price: "",
  payment_mode: "pay_later",
};

function formFromClinic(c: Clinic): ClinicForm {
  return {
    title: c.title,
    description: c.description ?? "",
    skill_level: c.skill_level ?? "",
    clinic_date: c.clinic_date,
    start_time: t5(c.start_time),
    end_time: t5(c.end_time),
    location_id: c.location_id ?? "",
    capacity: String(c.capacity),
    price: c.price_cents ? String(c.price_cents / 100) : "",
    payment_mode: c.payment_mode,
  };
}

function ClinicsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const promote = useServerFn(promoteAfterCapacityChange);
  const startOnboarding = useServerFn(startConnectOnboarding);
  const refreshConnect = useServerFn(refreshConnectStatus);
  const [dialog, setDialog] = useState<{ open: boolean; editing?: Clinic }>({ open: false });
  const [onboarding, setOnboarding] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const gate = useTierGate();
  const openNewDialog = () => {
    if (!gate.canCreateClinic) {
      setShowUpgrade(true);
      return;
    }
    setDialog({ open: true });
  };

  const connectQ = useQuery({
    queryKey: ["connect-status", user?.id],
    enabled: !!user,
    queryFn: async () => (await refreshConnect({})) as ConnectStatus,
    staleTime: 60 * 1000,
  });

  async function enablePayments() {
    setOnboarding(true);
    try {
      const res = await startOnboarding({ data: { origin: window.location.origin } });
      if (res.ok) {
        window.location.href = res.url;
      } else {
        toast.error(
          res.error === "gateway_blocked"
            ? "Stripe Connect isn't reachable yet — a STRIPE_SECRET_KEY needs to be added in Lovable Cloud secrets."
            : res.message
              ? `Stripe: ${res.message}`
              : "Couldn't start Stripe onboarding — try again.",
          { duration: 12000 },
        );
        setOnboarding(false);
      }
    } catch {
      toast.error("Couldn't start Stripe onboarding — try again.");
      setOnboarding(false);
    }
  }

  // ---- Queries ------------------------------------------------------------

  const clinicsQ = useQuery({
    queryKey: ["coach-clinics", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clinics")
        .select("*, clinic_registrations(status)")
        .eq("coach_id", user!.id)
        .order("clinic_date", { ascending: false })
        .order("start_time");
      if (error) throw error;
      return (data as unknown as ClinicRow[]) ?? [];
    },
  });

  const locationsQ = useQuery({
    queryKey: ["clinic-locations", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coach_locations")
        .select("id, name")
        .eq("coach_id", user!.id)
        .order("name");
      if (error) throw error;
      return (data as LocationLite[]) ?? [];
    },
  });

  const clinics = clinicsQ.data ?? [];
  const locations = useMemo(() => locationsQ.data ?? [], [locationsQ.data]);
  const locName = useMemo(() => new Map(locations.map((l) => [l.id, l.name])), [locations]);

  const todayIso = isoDatePlus(0); // local date — UTC would misfile tonight's clinic after ~5pm PT
  const upcoming = clinics
    .filter((c) => c.clinic_date >= todayIso && c.status !== "cancelled")
    .sort((a, b) => a.clinic_date.localeCompare(b.clinic_date));
  const past = clinics.filter((c) => c.clinic_date < todayIso || c.status === "cancelled");

  // ---- Mutations ----------------------------------------------------------

  const saveClinic = useMutation({
    mutationFn: async (vars: { form: ClinicForm; editing?: Clinic }) => {
      const f = vars.form;
      const priceCents = Math.round(Number(f.price || "0") * 100);
      const row = {
        title: f.title.trim(),
        description: f.description.trim() || null,
        skill_level: f.skill_level || null,
        clinic_date: f.clinic_date,
        start_time: f.start_time,
        end_time: f.end_time,
        location_id: f.location_id || null,
        capacity: Number(f.capacity),
        price_cents: priceCents,
        payment_mode: f.payment_mode,
      };
      if (vars.editing) {
        const { error } = await supabase
          .from("clinics")
          .update(row as never)
          .eq("id", vars.editing.id);
        if (error) throw error;
        // A capacity bump can free seats — promote waitlisted attendees.
        if (row.capacity > vars.editing.capacity) {
          const res = await promote({ data: { clinicId: vars.editing.id } });
          if (res.promoted > 0) {
            toast.success(
              `${res.promoted} ${res.promoted === 1 ? "person" : "people"} moved up from the waitlist`,
            );
          }
        }
      } else {
        const { error } = await supabase
          .from("clinics")
          .insert({ coach_id: user!.id, timezone: detectTimezone(), ...row } as never);
        if (error) throw error;
      }
    },
    onSuccess: (_d, vars) => {
      toast.success(vars.editing ? "Clinic updated" : "Clinic created");
      setDialog({ open: false });
      qc.invalidateQueries({ queryKey: ["coach-clinics", user?.id] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to save clinic"),
  });

  const setStatus = useMutation({
    mutationFn: async (vars: { id: string; status: "published" | "draft" }) => {
      const { error } = await supabase
        .from("clinics")
        .update({ status: vars.status } as never)
        .eq("id", vars.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      toast.success(
        vars.status === "published" ? "Clinic published — link is live" : "Clinic unpublished",
      );
      qc.invalidateQueries({ queryKey: ["coach-clinics", user?.id] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  const duplicate = useMutation({
    mutationFn: async (c: Clinic) => {
      const { error } = await supabase.from("clinics").insert({
        coach_id: user!.id,
        location_id: c.location_id,
        title: c.title,
        description: c.description,
        skill_level: c.skill_level,
        clinic_date: c.clinic_date,
        start_time: c.start_time,
        end_time: c.end_time,
        timezone: c.timezone,
        capacity: c.capacity,
        price_cents: c.price_cents,
        currency: c.currency,
        payment_mode: c.payment_mode,
        payment_window_hours: c.payment_window_hours,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Clinic duplicated as a draft — set the new date, then publish.");
      qc.invalidateQueries({ queryKey: ["coach-clinics", user?.id] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to duplicate"),
  });

  async function copyLink(c: Clinic) {
    await navigator.clipboard.writeText(publicClinicUrl(c.public_token));
    toast.success("Signup link copied");
  }

  return (
    <AppShell>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tight text-foreground md:text-5xl">
            Clinics
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Group sessions with a public signup page, capacity, and waitlist.
          </p>
        </div>
        <Button onClick={openNewDialog}>
          <Plus className="h-4 w-4 mr-1" /> New clinic
        </Button>
      </div>

      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-brand-text" />
            <div>
              <p className="text-sm font-semibold">
                Online payments
                {connectQ.data?.testMode && connectQ.data.connected && (
                  <Badge variant="secondary" className="ml-2">
                    Test mode
                  </Badge>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {connectQ.data && !connectQ.data.subscription_ok
                  ? "Paused — collecting online payments requires an active Clinics plan."
                  : connectQ.data?.charges_enabled
                    ? "Connected — attendees can pay online to reserve their spot."
                    : connectQ.data?.connected
                      ? "Setup started but not finished — payments stay off until Stripe approves the account."
                      : "Connect a Stripe account to let attendees pay when they register."}
              </p>
            </div>
          </div>
          {!connectQ.data?.charges_enabled && (
            <Button size="sm" variant="outline" onClick={enablePayments} disabled={onboarding}>
              {onboarding && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
              {connectQ.data?.connected ? "Finish setup" : "Enable online payments"}
            </Button>
          )}
        </div>
        {connectQ.data && !connectQ.data.subscription_ok && (
          <div className="mt-3 rounded-[10px] border border-warning/40 bg-warning/10 p-3">
            <p className="text-xs text-foreground">
              {connectQ.data.charges_enabled
                ? "Your Clinics plan has lapsed — online payment is paused for your clinics. Renew to start collecting again."
                : "You can finish Stripe verification now, but collecting online payments requires the Clinics plan."}{" "}
              <Link to="/app/settings/billing" className="font-semibold underline">
                View billing
              </Link>
            </p>
          </div>
        )}
      </Card>

      {clinicsQ.isLoading ? (
        <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : clinics.length === 0 ? (
        <Card className="mt-6 p-8 text-center">
          <GraduationCap className="mx-auto h-10 w-10 text-muted-foreground/50" />
          <h2 className="mt-3 text-sm font-semibold">Run your first clinic</h2>
          <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">
            Create a group session, share the signup link, and let players grab spots. Overflow goes
            to a waitlist automatically.
          </p>
          <Button className="mt-4" onClick={openNewDialog}>
            <Plus className="h-4 w-4 mr-1" /> New clinic
          </Button>
        </Card>
      ) : (
        <>
          <ClinicList
            title="Upcoming"
            clinics={upcoming}
            locName={locName}
            onEdit={(c) => setDialog({ open: true, editing: c })}
            onPublish={(c) =>
              setStatus.mutate({
                id: c.id,
                status: c.status === "published" ? "draft" : "published",
              })
            }
            onDuplicate={(c) => duplicate.mutate(c)}
            onCopyLink={copyLink}
          />
          {past.length > 0 && (
            <ClinicList
              title="Past & cancelled"
              clinics={past}
              locName={locName}
              onDuplicate={(c) => duplicate.mutate(c)}
            />
          )}
        </>
      )}

      <ClinicDialog
        key={dialog.editing?.id ?? (dialog.open ? "new" : "closed")}
        open={dialog.open}
        editing={dialog.editing}
        locations={locations}
        saving={saveClinic.isPending}
        payOnlineEnabled={
          !!connectQ.data?.charges_enabled && !!connectQ.data?.subscription_ok
        }
        payOnlineBlockedReason={
          connectQ.data && !connectQ.data.subscription_ok
            ? " (requires the Clinics plan)"
            : " (enable online payments first)"
        }
        onClose={() => setDialog({ open: false })}
        onSave={(form) => saveClinic.mutate({ form, editing: dialog.editing })}
      />
      <UpgradeModal
        open={showUpgrade}
        onOpenChange={setShowUpgrade}
        tier="clinics"
        feature="Clinics"
        description="Running group clinics with a public signup page, waitlist, and online payments requires the Clinics plan."
      />
    </AppShell>
  );
}

function activeCount(c: ClinicRow) {
  return c.clinic_registrations.filter(
    (r) => r.status === "registered" || r.status === "pending_payment",
  ).length;
}

function waitlistCount(c: ClinicRow) {
  return c.clinic_registrations.filter((r) => r.status === "waitlisted").length;
}

function ClinicList(props: {
  title: string;
  clinics: ClinicRow[];
  locName: Map<string, string>;
  onEdit?: (c: ClinicRow) => void;
  onPublish?: (c: ClinicRow) => void;
  onDuplicate?: (c: ClinicRow) => void;
  onCopyLink?: (c: ClinicRow) => void;
}) {
  if (props.clinics.length === 0) return null;
  return (
    <div className="mt-6">
      <h2 className="text-sm font-semibold text-muted-foreground">{props.title}</h2>
      <div className="mt-2 space-y-2">
        {props.clinics.map((c) => (
          <Card key={c.id} className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Link
                    to="/app/clinics/$clinicId"
                    params={{ clinicId: c.id }}
                    className="truncate text-sm font-semibold hover:underline"
                  >
                    {c.title}
                  </Link>
                  <StatusBadge clinic={c} />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {fmtBookingDate(c.clinic_date)} · {fmtSlotTime(c.start_time)}–
                  {fmtSlotTime(c.end_time)}
                  {c.location_id && props.locName.get(c.location_id) && (
                    <>
                      {" "}
                      · <MapPin className="inline h-3 w-3" /> {props.locName.get(c.location_id)}
                    </>
                  )}
                </p>
                <p className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {activeCount(c)}/{c.capacity}
                    {waitlistCount(c) > 0 && ` (+${waitlistCount(c)} waitlisted)`}
                  </span>
                  <span>
                    {c.price_cents > 0 ? fmtClinicPrice(c.price_cents, c.currency) : "Free"} ·{" "}
                    {PAYMENT_MODE_LABELS[c.payment_mode]}
                  </span>
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {props.onCopyLink && c.status === "published" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => props.onCopyLink!(c)}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <a href={publicClinicUrl(c.public_token)} target="_blank" rel="noreferrer">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </Button>
                  </>
                )}
                {props.onEdit && c.status !== "cancelled" && (
                  <Button size="sm" variant="outline" onClick={() => props.onEdit!(c)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
                {props.onDuplicate && (
                  <Button size="sm" variant="outline" onClick={() => props.onDuplicate!(c)}>
                    <CopyPlus className="h-3.5 w-3.5" />
                  </Button>
                )}
                {props.onPublish && (
                  <Button
                    size="sm"
                    variant={c.status === "published" ? "ghost" : "default"}
                    onClick={() => props.onPublish!(c)}
                  >
                    {c.status === "published" ? "Unpublish" : "Publish"}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ clinic }: { clinic: Clinic }) {
  if (clinic.status === "cancelled") return <Badge variant="destructive">Cancelled</Badge>;
  if (clinic.status === "draft") return <Badge variant="secondary">Draft</Badge>;
  return <Badge className="bg-success/10 text-success hover:bg-success/10">Live</Badge>;
}

function ClinicDialog(props: {
  open: boolean;
  editing?: Clinic;
  locations: LocationLite[];
  saving: boolean;
  payOnlineEnabled: boolean;
  payOnlineBlockedReason: string;
  onClose: () => void;
  onSave: (form: ClinicForm) => void;
}) {
  const [form, setForm] = useState<ClinicForm>(
    props.editing ? formFromClinic(props.editing) : EMPTY_FORM,
  );
  const set = (patch: Partial<ClinicForm>) => setForm((f) => ({ ...f, ...patch }));

  const priceCents = Math.round(Number(form.price || "0") * 100);
  const validationError = !form.title.trim()
    ? "Give the clinic a title."
    : !form.clinic_date
      ? "Pick a date."
      : form.start_time >= form.end_time
        ? "End time must be after start time."
        : !form.capacity || Number(form.capacity) < 1 || Number(form.capacity) > 200
          ? "Capacity must be between 1 and 200."
          : form.payment_mode !== "free" && priceCents <= 0
            ? "Set a price, or mark the clinic as free."
            : null;

  return (
    <Dialog open={props.open} onOpenChange={(o) => !o && props.onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{props.editing ? "Edit clinic" : "New clinic"}</DialogTitle>
          <DialogDescription>
            {props.editing
              ? "Changes show on the public page immediately."
              : "Clinics start as drafts — publish when you're ready to share the link."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={form.title}
              maxLength={120}
              placeholder="Saturday Beginner Clinic"
              onChange={(e) => set({ title: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Description (shown on the signup page)</Label>
            <Textarea
              value={form.description}
              rows={3}
              placeholder="What you'll cover, what to bring…"
              onChange={(e) => set({ description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={form.clinic_date}
                onChange={(e) => set({ clinic_date: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Skill level</Label>
              <Select value={form.skill_level} onValueChange={(v) => set({ skill_level: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  {SKILL_LEVELS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Starts</Label>
              <Input
                type="time"
                value={form.start_time}
                onChange={(e) => set({ start_time: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Ends</Label>
              <Input
                type="time"
                value={form.end_time}
                onChange={(e) => set({ end_time: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Location</Label>
              <Select value={form.location_id} onValueChange={(v) => set({ location_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  {props.locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Capacity</Label>
              <Input
                type="number"
                min={1}
                max={200}
                value={form.capacity}
                onChange={(e) => set({ capacity: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Price per player ($)</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={form.price}
                placeholder="0"
                disabled={form.payment_mode === "free"}
                onChange={(e) => set({ price: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Payment</Label>
              <Select
                value={form.payment_mode}
                onValueChange={(v) =>
                  set({ payment_mode: v as ClinicPaymentMode, ...(v === "free" && { price: "" }) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">{PAYMENT_MODE_LABELS.free}</SelectItem>
                  <SelectItem value="pay_later">{PAYMENT_MODE_LABELS.pay_later}</SelectItem>
                  <SelectItem value="pay_online" disabled={!props.payOnlineEnabled}>
                    {PAYMENT_MODE_LABELS.pay_online}
                    {props.payOnlineEnabled ? "" : props.payOnlineBlockedReason}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            disabled={props.saving || !!validationError}
            title={validationError ?? undefined}
            onClick={() => props.onSave(form)}
          >
            {props.saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {props.editing ? "Save changes" : "Create draft"}
          </Button>
        </DialogFooter>
        {validationError && (
          <p className="text-xs text-muted-foreground text-right">{validationError}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
